/** @jsxImportSource ./simboluri-jsx */
// FIȘA SOLUȚIEI (mock-ui/design/StrSolutieRaport.dc.html): ce a promis și ce
// a livrat soluția pe care te uiți. Datele sunt raportul Pasului 19 al
// rândului din cale (`/structura/:sol/pas/fisa`). Aceleași date pe care le
// arată §2 din pagina Pasului 19; aici ca fișă de sine stătătoare, cu
// esențialul sus și fără depanare.

import { StepState } from "./tipuri";
import { useT } from "../i18n";
import type { Cheie } from "../i18n";
import { nr } from "./elemente";
import { hashElement, hashSectiune } from "./legaturi";
import type { SolutieRand } from "../nav/api";
import { numeFaza, numeRand } from "../nav/selectie";
import { desfacereaScorului, explicaScorul } from "../scor";
import { categoriiDinStare, explicaPicatele } from "../picate";

type RandModificare = {
  cheie: string;
  verificare?: string;
  element?: string;
  RE?: number | null;
  abordare?: number | null;
  abordari_total?: number;
  modificare?: string;
  stare?: string;
  ops?: Record<string, unknown>[] | null;
};

const STARE_MOD: Record<string, { cls: string; text: Cheie }> = {
  aplicata: { cls: "ob-c-ok", text: "struct.fs.stare_aplicata" },
  acoperita: { cls: "ob-c-mut", text: "struct.fs.stare_acoperita" },
  epuizata: { cls: "ob-c-amb", text: "struct.fs.stare_epuizata" },
  refuzata: { cls: "ob-c-bad", text: "struct.fs.stare_refuzata" },
};

export default function FisaSolutiePage({
  id,
  solutie,
  steps,
  rand,
}: {
  id: string;
  solutie?: string | null;
  steps: Record<string, StepState>;
  /** rândul privit (din registru) */
  rand: SolutieRand | null;
}) {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const r = steps["optimizare_structura"]?.result as any;
  const raport = r?.raport_solutie;
  const final = raport?.final ?? r?.stare_finala ?? null;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  const t = useT();
  const numele = rand ? numeRand(rand) : "—";
  // scorul și verdictul: ale raportului Pasului 19, altfel ale registrului
  const scorFisa: number | null = r?.scor ?? rand?.structura?.cifre.scor ?? null;
  const partiFisa = r?.scor_parti ?? null;
  const verdictFisa: string | null = r?.verdict ?? null;

  if (!raport && !final) {
    return (
      <div className="ob-pagina ob-pagina-ingusta">
        <div className="ob-pipe-head"><h2>{t("struct.fs.titlu")}</h2></div>
        <div className="sg-muted">
          {t("struct.fs.gol")}{" "}
          <a href={hashSectiune(id, "pipeline", solutie ?? null)}>
            {t("struct.act.link_pipeline")}
          </a>
        </div>
      </div>
    );
  }

  const modificari: RandModificare[] = raport?.modificari ?? [];
  const registru: {
    cheie?: string; parghie?: string; promis?: number; livrat?: number;
    verdict?: string; demotata?: boolean;
  }[] = raport?.registru_raport ?? [];
  const amanate: { verificare?: string; element?: string; motiv?: string }[] =
    raport?.amanate ?? [];
  const netratate: { verificare?: string; tip?: string; element?: string; motiv?: string }[] =
    raport?.netratate ?? [];
  const totalVerificari = (final?.acoperire ?? []).reduce(
    (s: number, a: { nr_verificari?: number }) => s + (a.nr_verificari ?? 0),
    0
  );
  const cost = final?.cost?.total_eur ?? final?.cost?.total_direct_eur;
  const costPlecare =
    raport?.plecare?.cost?.total_eur ?? raport?.plecare?.cost?.total_direct_eur;

  const nrPeStare = (st: string) => modificari.filter((m) => m.stare === st).length;

  return (
    <div className="ob-pagina" style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div className="ob-pas-antet" style={{ paddingTop: 0 }}>
        <a href={hashSectiune(id, "solutii", solutie ?? null)}>
          {t("struct.fs.solutii")}
        </a>
        <span>/</span>
        <b style={{ color: "var(--ink)" }}>
          {t("struct.fs.fisa_nume", { nume: numele })}
        </b>
      </div>

      {/* antetul fișei */}
      <div className="ob-card" style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <b className="ob-disp" style={{ fontSize: 22 }}>{numele}</b>
            {/* faza rândului: macro rulează pe rețeaua de probă, detaliul pe
                cea plină — fidelitatea se deduce din fază, nu mai are cip */}
            {rand?.faza && rand.faza !== "schita" && (
              <span className="ob-chip ob-c-mut">{numeFaza(rand.faza)}</span>
            )}
          </div>
          <span style={{ color: "var(--mut)", fontSize: 12 }}>
            {raport?.plecare?.solutie
              ? t("struct.fs.din_solutia", { solutie: raport.plecare.solutie })
              : ""}
            {raport?.rezolutie?.h_mesh_m
              ? t("struct.fs.rezolutie", { n: nr(raport.rezolutie.h_mesh_m, 2) })
              : ""}
            {raport?.durata_min
              ? t("struct.fs.durata", { n: nr(raport.durata_min, 1) }) : ""}
          </span>
        </div>
        <div style={{ width: 1, height: 44, background: "var(--line)" }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span className="ob-lbl">{t("struct.fs.picate")}</span>
          <span title={explicaPicatele(final?.nr_picate ?? 0, categoriiDinStare(final))}>
            <b className="ob-disp" style={{ fontSize: 22, color: final?.nr_picate ? "var(--bad)" : "var(--ok)" }}>
              {nr(final?.nr_picate ?? 0)}
            </b>
            {totalVerificari ? (
              <span style={{ color: "var(--mut)", fontSize: 12 }}>
                {t("struct.fs.din_total", { n: nr(totalVerificari) })}
              </span>
            ) : null}
          </span>
        </div>
        {cost != null && (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span className="ob-lbl">{t("struct.fs.cost")}</span>
            <span>
              <b className="ob-disp" style={{ fontSize: 22 }}>{nr(cost)}</b>
              <span style={{ color: "var(--mut)", fontSize: 12 }}>
                {" "}€{costPlecare != null && costPlecare !== cost
                  ? t("struct.fs.fata_de_plecare", {
                      semn: cost < costPlecare ? "−" : "+",
                      suma: nr(Math.abs(cost - costPlecare)),
                    })
                  : ""}
              </span>
            </span>
          </div>
        )}
        {scorFisa != null && (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}
               title={explicaScorul(scorFisa, partiFisa)}>
            <span className="ob-lbl">{t("struct.fs.scor")}</span>
            <b className="ob-disp" style={{ fontSize: 22 }}>
              {nr(scorFisa, 1)}
            </b>
            {/* DESFACEREA: «37,2» nu spune nimic dacă nu se vede din ce e
                făcut — cât din el e verificări picate, cât cost și cât
                diversitate de elemente. */}
            {partiFisa && (
              <span className="sg-mono" style={{ color: "var(--mut)", fontSize: 11 }}>
                {desfacereaScorului(partiFisa)}
              </span>
            )}
          </div>
        )}
        {verdictFisa && (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span className="ob-lbl">{t("struct.fs.verdict")}</span>
            <b style={{ fontSize: 13 }}>{verdictFisa}</b>
          </div>
        )}
        <span style={{ flex: 1 }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <a className="ob-chip ob-c-acc" style={{ textDecoration: "none" }}
             href={hashSectiune(id, "verificari", solutie ?? null)}>
            {t("struct.fs.picatele")}
          </a>
          <a className="ob-chip ob-c-mut" style={{ textDecoration: "none" }}
             href={hashSectiune(id, "optimizare_structura", solutie ?? null)}>
            {t("struct.fs.raport")}
          </a>
        </div>
      </div>

      {/* ce s-a modificat efectiv */}
      {modificari.length > 0 && (
        <div className="ob-card" style={{ overflow: "hidden", marginBottom: 14 }}>
          <div style={{ padding: "11px 14px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <b>{t("struct.fs.modificat")}</b>
            <span className="ob-acasa-sub">{t("struct.fs.op_uri")}</span>
            <span style={{ flex: 1 }} />
            {nrPeStare("aplicata") > 0 && (
              <span className="ob-chip ob-c-ok">
                {t("struct.fs.aplicate", { n: nrPeStare("aplicata") })}
              </span>
            )}
            {nrPeStare("acoperita") > 0 && (
              <span className="ob-chip ob-c-mut">
                {t("struct.fs.acoperite", { n: nrPeStare("acoperita") })}
              </span>
            )}
            {nrPeStare("epuizata") > 0 && (
              <span className="ob-chip ob-c-amb">
                {t("struct.fs.epuizate", { n: nrPeStare("epuizata") })}
              </span>
            )}
            {nrPeStare("refuzata") > 0 && (
              <span className="ob-chip ob-c-bad">
                {t("struct.fs.refuzate", { n: nrPeStare("refuzata") })}
              </span>
            )}
          </div>
          <table className="ob-sol-tabel">
            <thead>
              <tr>
                <th style={{ width: 80 }}>{t("verificari.col_element")}</th>
                <th>{t("struct.fs.col_verificarea")}</th>
                <th style={{ width: 70, textAlign: "right" }}>
                  {t("struct.fs.col_lipsea")}
                </th>
                <th style={{ width: 80 }}>{t("struct.fs.col_abordarea")}</th>
                <th>{t("struct.fs.col_schimbat")}</th>
                <th style={{ width: 170 }}>{t("struct.fs.col_stare")}</th>
              </tr>
            </thead>
            <tbody>
              {modificari.map((m, i) => {
                const chip = STARE_MOD[m.stare ?? ""];
                const ePlan = /^(GR|ST|PB|OP)\d/.test(String(m.element ?? ""));
                return (
                  <tr key={i}>
                    <td>
                      {ePlan ? (
                        <a className="ob-ref" href={hashElement(id, String(m.element), solutie)}>
                          {m.element}
                        </a>
                      ) : (
                        <span className="sg-mono" style={{ fontSize: 11 }}>{m.element ?? "—"}</span>
                      )}
                    </td>
                    <td style={{ fontSize: 12 }}>{m.verificare ?? m.cheie}</td>
                    <td className="sg-mono" style={{ textAlign: "right", fontSize: 12, color: "var(--bad)" }}>
                      {m.RE != null ? nr(m.RE, 2) : "—"}
                    </td>
                    <td style={{ fontSize: 11, color: "var(--mut)" }}
                        title={t("struct.fs.abordare_titlu")}>
                      {m.abordare != null && m.abordari_total
                        ? t("struct.fs.abordare", {
                            n: m.abordare, total: m.abordari_total,
                          })
                        : "—"}
                    </td>
                    <td style={{ fontSize: 12, color: "var(--mut)" }}>{m.modificare ?? ""}</td>
                    <td>
                      <span className={`ob-chip ${chip?.cls ?? "ob-c-mut"}`}
                            style={{ fontSize: 10 }}>
                        {chip ? t(chip.text) : m.stare ?? "—"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* promis vs livrat */}
        {registru.length > 0 && (
          <div className="ob-card" style={{ flex: "1 1 420px", minWidth: 0, overflow: "hidden" }}>
            <div style={{ padding: "11px 14px", display: "flex", gap: 8, alignItems: "center" }}>
              <b>{t("struct.fs.promis_livrat")}</b>
              <span className="ob-acasa-sub">{t("struct.fs.promis_sub")}</span>
            </div>
            <table className="ob-sol-tabel">
              <thead>
                <tr>
                  <th>{t("struct.fs.col_parghia")}</th>
                  <th style={{ width: 120, textAlign: "right" }}>
                    {t("struct.fs.col_promis")}
                  </th>
                  <th style={{ width: 90 }}>{t("struct.fs.verdict")}</th>
                </tr>
              </thead>
              <tbody>
                {registru.map((x, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: 12 }}>
                      <span className="sg-mono" style={{ fontSize: 11 }}>{x.cheie}</span>
                      {x.parghie ? <span style={{ color: "var(--mut)" }}> · {x.parghie}</span> : null}
                    </td>
                    <td className="sg-mono" style={{ textAlign: "right", fontSize: 12 }}>
                      {x.promis != null ? nr(x.promis, 1) : "—"} → {x.livrat != null ? nr(x.livrat, 1) : "—"}
                    </td>
                    <td>
                      {x.demotata ? (
                        <span className="ob-chip ob-c-bad" style={{ fontSize: 10 }}
                              title={t("struct.fs.demotata_titlu")}>
                          {t("struct.fs.demotata")}
                        </span>
                      ) : (
                        <span className="ob-chip ob-c-mut" style={{ fontSize: 10 }}>{x.verdict ?? "—"}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* amânate + netratate + de ce s-a oprit */}
        <div style={{ flex: "1 1 320px", display: "flex", flexDirection: "column", gap: 12 }}>
          {amanate.length > 0 && (
            <div className="ob-card" style={{ padding: "13px 15px", display: "flex", flexDirection: "column", gap: 6 }}>
              <span className="ob-lbl">
                {t("struct.fs.amanate", { n: amanate.length })}
              </span>
              {amanate.map((a, i) => (
                <div key={i} style={{ fontSize: 12 }}>
                  <b>{a.verificare}</b>
                  {a.element ? <span className="sg-mono" style={{ fontSize: 11 }}> · {a.element}</span> : null}
                  {a.motiv ? <div style={{ color: "var(--mut)", fontSize: 11 }}>{a.motiv}</div> : null}
                </div>
              ))}
            </div>
          )}
          {netratate.length > 0 && (
            <div className="ob-card" style={{ padding: "13px 15px", display: "flex", flexDirection: "column", gap: 6 }}>
              <span className="ob-lbl">
                {t("struct.fs.netratate", { n: netratate.length })}
              </span>
              {netratate.map((a, i) => (
                <div key={i} style={{ fontSize: 12 }}>
                  <b>{a.verificare ?? a.tip}</b>
                  {a.element ? <span className="sg-mono" style={{ fontSize: 11 }}> · {a.element}</span> : null}
                  {a.motiv ? <div style={{ color: "var(--mut)", fontSize: 11 }}>{a.motiv}</div> : null}
                </div>
              ))}
            </div>
          )}
          {raport?.nota_plan && (
            <div className="ob-card" style={{ padding: "13px 15px", display: "flex", flexDirection: "column", gap: 6 }}>
              <span className="ob-lbl">{t("struct.fs.de_ce_oprit")}</span>
              <div style={{ fontSize: 12, color: "var(--mut)" }}>{raport.nota_plan}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
